<template>
  <Modal v-if="shown" v-model="shown" :title="teamName" data-test="egm-modal">
    <div class="modal-grid">
      <div class="members-column">
        <h4 class="underline">Current members</h4>
        <div class="search-input">
          <input
            type="search"
            class="form-control"
            placeholder="Search members..."
            v-model="searchMembersText"
            data-test="egm-search-members"
          />
        </div>
        <div class="margin-top">
          <ul class="members-list" data-test="egm-members-list">
            <li v-for="member in filteredMembers" :key="member.id">
              <button
                class="btn btn-default-close justify-left"
                style="width: 100%"
                @click="removeMember(member.id)"
                data-test="egm-member-button"
              >
                {{ getMemberFullName(member) }}
              </button>
            </li>
          </ul>
        </div>
        <div class="row" v-if="errorText">
          <br />
          <div class="col-sm-12">
            <p class="text-danger text-left" data-test="egm-errors">
              {{ errorText }}
            </p>
          </div>
        </div>
      </div>
      <div class="add-members-column">
        <SearchUsers
          :existingMembers="memberList"
          :organizationId="organizationId"
          @select-user="addMember"
          @update-search-state="updateSearchState"
        >
          <h4 class="underline">Add more members</h4>
        </SearchUsers>
      </div>
    </div>
    <template #footer>
      <button class="btn btn-default" @click="close()" data-test="egm-close-modal">Close</button>
      <button
        class="btn btn-primary"
        :disabled="!editedUsers.length || isSaving || isLoading"
        @click="submitEditGroupMembers(memberList)"
        data-test="egm-edit-group-members"
      >
        {{ isSaving ? "Saving..." : "Confirm" }}
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Modal } from "uiv";
import { computed, ref } from "vue";
import {
  EditGroupMembersApiModel,
  ResultApiModel,
  ShortUserDetailsApiModel,
} from "../../../api-models";
import { csContext } from "../../../services/cs-context";
import { HttpMethods, useAxios } from "../../../composables/api";
import SearchUsers from "./accredited-user-area-search-users.vue";

const props = defineProps<{
  showEditMembers: boolean;
  existingMembers: ShortUserDetailsApiModel[];
  teamId: string;
  teamName: string | undefined;
  organizationId?: string;
}>();

const emit = defineEmits(["update:showEditMembers"]);

const searchMembersText = ref("");
const memberList = ref<ShortUserDetailsApiModel[]>(props.existingMembers);
const editedUsers = ref<ShortUserDetailsApiModel[]>([]);
const isLoading = ref(false);
const isSaving = ref(false);
const errorText = ref<string | null>(null);

const shown = computed({
  get: () => props.showEditMembers,
  set: (value) => emit("update:showEditMembers", value),
});

const filteredMembers = computed(() => {
  const searchQuery = searchMembersText.value.toLowerCase().trim();
  return memberList.value.filter((m) => {
    return (
      m.firstName.toLowerCase().includes(searchQuery) ||
      m.lastName.toLowerCase().includes(searchQuery)
    );
  });
});

const close = (): void => {
  let isRefreshNeeded = false;
  if (editedUsers.value.length) {
    isRefreshNeeded = true;
  }
  emit("update:showEditMembers", isRefreshNeeded);
};

const submitEditGroupMembers = (updatedMembers: ShortUserDetailsApiModel[]): void => {
  errorText.value = null;
  isSaving.value = true;
  const ids = updatedMembers.map((m) => {
    return m.id;
  });
  const groupEditModel: EditGroupMembersApiModel = {
    memberIds: ids,
    triggeringUserId: csContext.user.id,
  };
  const { data, errorStatus, promise } = useAxios<ResultApiModel>(
    `/api/accrediteduserarea/editmembers/${props.teamId}`,
    {
      isFatal: false,
      httpMethod: "POST" as HttpMethods,
      data: groupEditModel,
    },
  );
  promise.then(() => {
    if (errorStatus.value) {
      errorText.value = "There was a problem editing the group members";
    }

    isSaving.value = false;

    if (data.value?.success) {
      close();
    }
  });
};

const hasUserAlreadyBeenEdited = (member: ShortUserDetailsApiModel): boolean => {
  return (
    editedUsers.value.filter((u) => {
      return u.id === member.id;
    }).length > 0
  );
};

const removeMember = (id: string): void => {
  const updatedMembers = memberList.value.filter((m) => {
    return m.id !== id;
  });
  const memberToRemoveFromTeam = memberList.value.find((m) => {
    return m.id === id;
  }) as ShortUserDetailsApiModel;

  memberList.value = updatedMembers;

  if (hasUserAlreadyBeenEdited(memberToRemoveFromTeam)) {
    editedUsers.value = editedUsers.value.filter((u) => {
      return u.id !== memberToRemoveFromTeam.id;
    });
  } else {
    editedUsers.value.push(memberToRemoveFromTeam);
  }
};

const addMember = (member: ShortUserDetailsApiModel): void => {
  const updatedMembers = memberList.value;
  updatedMembers.push(member);

  memberList.value = updatedMembers;

  if (hasUserAlreadyBeenEdited(member)) {
    editedUsers.value = editedUsers.value.filter((u) => {
      return u.id !== member.id;
    });
  } else {
    editedUsers.value.push(member);
  }
};

const getMemberFullName = (member: ShortUserDetailsApiModel): string => {
  return `${member.firstName} ${member.lastName}`;
};

const updateSearchState = (isSearching: boolean): void => {
  isLoading.value = isSearching;
};
</script>

<style scoped lang="less">
@import "../../../styles/bootstrap/variables.less";

.modal-grid {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1rem;
  @media (max-width: @screen-md-max) {
    grid-template-columns: 1fr;
  }
}

.members-column {
  width: 100%;
}

.underline {
  border-bottom: 1px solid @charismatic-color;
}

.margin-top {
  margin-top: 20px;
}

.search-input {
  width: 100%;
  input {
    max-width: 100%;
  }
}

.members-list {
  list-style: none;
  padding-left: 0;
  li {
    width: 100%;
    button {
      white-space: normal;
      word-wrap: break-word;
    }
  }
}

.justify-left {
  justify-content: flex-start;
}

.email {
  color: @form-text-color;
}
</style>
